<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.sale_order.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right" v-if="isEditModernTrade">
          <base-button size="md" type="neutral" @click="openCreate">
            <i class="fa fa-plus"></i> {{ $t("modern_trade.sale_order.create") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row">
            <div class="col-md-3">
              <base-input>
                <el-select
                  v-model="filterCompany"
                  :placeholder="$t('modern_trade.sale_order.company')"
                  clearable
                  filterable
                  @change="onFilter"
                  class="w-100"
                >
                  <el-option
                    v-for="c in companies"
                    :key="c.id"
                    :label="c.name"
                    :value="c.id"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-2">
              <base-input>
                <el-select
                  v-model="filterSaleType"
                  :placeholder="$t('modern_trade.sale_order.sale_type')"
                  clearable
                  @change="onFilter"
                  class="w-100"
                >
                  <el-option :label="$t('modern_trade.sale_order.sale_type_consignment')" :value="1"></el-option>
                  <el-option :label="$t('modern_trade.sale_order.sale_type_outright')" :value="2"></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-2">
              <base-input>
                <el-select
                  v-model="filterStatus"
                  :placeholder="$t('modern_trade.sale_order.status')"
                  clearable
                  @change="onFilter"
                  class="w-100"
                >
                  <el-option
                    v-for="s in statusOptions"
                    :key="s.value"
                    :label="s.label"
                    :value="s.value"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-5">
              <base-input>
                <el-date-picker
                  v-model="dateRange"
                  type="daterange"
                  value-format="yyyy-MM-dd"
                  range-separator="-"
                  :start-placeholder="$t('modern_trade.sale_order.created_from')"
                  :end-placeholder="$t('modern_trade.sale_order.created_to')"
                  class="w-100"
                  @change="onFilter"
                ></el-date-picker>
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <el-input
                v-model="search"
                :placeholder="$t('search')"
                clearable
                @keyup.enter.native="onFilter"
              >
                <el-button slot="append" type="primary" @click="onFilter">
                  <i class="fa fa-search"></i> {{ $t("search") }}
                </el-button>
              </el-input>
            </div>
            <!-- round-19 Dev-C: global document-number search -->
            <div class="col-md-6">
              <mt-doc-search-box></mt-doc-search-box>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table :data="rows" row-key="id" header-row-class-name="thead-light">
            <el-table-column :label="$t('modern_trade.sale_order.so_no')" min-width="150">
              <template slot-scope="{ row }">
                <a href="javascript:void(0)" @click="goDetail(row.id)"><strong>{{ row.so_no }}</strong></a>
                <i
                  class="fa fa-file-pdf text-danger ml-2"
                  style="cursor:pointer"
                  :title="$t('modern_trade.sale_order.view_pdf')"
                  @click="openPoPdf(row)"
                ></i>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.customer')" min-width="240">
              <template slot-scope="{ row }">
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <div><strong>{{ row.company_name || row.company_id }}</strong></div>
                    <small class="text-muted">
                      {{ row.tax_id || "-" }}<span v-if="row.branch_code"> · {{ row.branch_code }}</span>
                    </small>
                  </div>
                  <base-button
                    size="sm"
                    type="secondary"
                    class="py-1 px-2 ml-2"
                    :title="$t('modern_trade.sale_order.view_customer')"
                    @click.native="openCustomer(row)"
                  >
                    <i class="fa fa-info-circle"></i>
                  </base-button>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.po_number')" min-width="120">
              <template slot-scope="{ row }">{{ row.po_number }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.net_amount')" min-width="120" align="right">
              <template slot-scope="{ row }">{{ money(row.net_amount) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.status')" min-width="120" align="center">
              <template slot-scope="{ row }">
                <span class="mt-pill" :style="mtStatusStyle(row.status)">{{ mtStatusLabel(row.status) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.created_at') || 'Created'" min-width="120">
              <template slot-scope="{ row }">{{ formatDate(row.created_at) }}</template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="90" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="info" @click.native="goDetail(row.id)">
                  <i class="fa fa-eye"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadData"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- Create Dialog -->
    <el-dialog
      :title="$t('modern_trade.sale_order.create')"
      :visible.sync="showDialog"
      width="900px"
      top="4vh"
      @closed="resetForm"
    >
      <div class="row">
        <div class="col-12">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.sale_order.company") }} *</label>
            <el-select
              v-model="form.company_id"
              filterable
              :placeholder="$t('modern_trade.sale_order.company')"
              class="w-100"
              @change="onCompanyChange"
            >
              <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label d-block">{{ $t("modern_trade.sale_order.sale_type") }} *</label>
            <el-select v-model="form.sale_type" class="w-100">
              <el-option :label="$t('modern_trade.sale_order.sale_type_consignment')" :value="1"></el-option>
              <el-option :label="$t('modern_trade.sale_order.sale_type_outright')" :value="2"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label d-block">{{ $t("modern_trade.sale_order.shipping_type") }} *</label>
            <el-select v-model="form.shipping_type" class="w-100">
              <el-option :label="$t('modern_trade.sale_order.shipping_type_pack')" :value="1"></el-option>
              <el-option :label="$t('modern_trade.sale_order.shipping_type_factory')" :value="2"></el-option>
              <el-option :label="$t('modern_trade.sale_order.shipping_type_express')" :value="3"></el-option>
            </el-select>
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.sale_order.po_number") }} *</label>
            <input v-model="form.po_number" class="form-control" type="text" />
          </div>
        </div>
        <div class="col-md-3">
          <div class="form-group">
            <label class="form-control-label d-block">{{ $t("modern_trade.sale_order.po_date") }}</label>
            <el-date-picker
              v-model="form.po_date"
              type="date"
              value-format="yyyy-MM-dd"
              :placeholder="$t('modern_trade.sale_order.po_date')"
              class="w-100"
            ></el-date-picker>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.sale_order.po_file") }} *</label>
            <input
              ref="poFileInput"
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.xlsx"
              class="form-control"
              @change="onPoFileChange"
            />
            <small class="text-muted">pdf / jpg / png / xlsx, ≤ 10MB</small>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group">
            <label class="form-control-label">{{ $t("modern_trade.sale_order.note") }}</label>
            <input v-model="form.note" class="form-control" type="text" />
          </div>
        </div>
      </div>

      <!-- Item picker -->
      <div class="card border mt-2">
        <div class="card-body">
          <h4 class="mb-3">{{ $t("modern_trade.sale_order.items") }}</h4>
          <div class="row align-items-end mb-1">
            <div class="col-md-8">
              <label class="form-control-label">{{ $t("modern_trade.sale_order.product") }}</label>
              <el-select
                v-model="picker.product_id"
                filterable
                remote
                reserve-keyword
                :placeholder="$t('modern_trade.sale_order.product')"
                :remote-method="searchProducts"
                :loading="productLoading"
                class="w-100"
                @visible-change="onProductDropdown"
              >
                <el-option
                  v-for="p in productOptions"
                  :key="p.id"
                  :label="(p.code ? p.code + ' - ' : '') + p.name"
                  :value="p.id"
                >
                  <span>{{ (p.code ? p.code + ' - ' : '') + p.name }}</span>
                  <span v-if="!isWmsLinked(p)" class="badge badge-danger ml-2">
                    <i class="fa fa-exclamation-triangle"></i> {{ $t('modern_trade.sale_order.no_sku_badge') }}
                  </span>
                </el-option>
              </el-select>
            </div>
            <div class="col-md-2">
              <label class="form-control-label">{{ $t("modern_trade.sale_order.qty") }}</label>
              <input v-model.number="picker.qty" type="number" min="1" class="form-control" />
            </div>
            <div class="col-md-2">
              <base-button type="primary" block class="m-0" @click="addItem">
                <i class="fa fa-plus"></i> {{ $t("add") || "Add" }}
              </base-button>
            </div>
          </div>
          <!-- hint below the row so it never affects the top/bottom alignment of the 3 controls -->
          <small class="text-danger d-block mb-3">
            <i class="fa fa-info-circle"></i> {{ $t('modern_trade.sale_order.no_sku_hint') }}
          </small>

          <!-- full-bleed: el-table วัดความกว้างจาก parent ตอน mount — ให้ wrapper ยืดชนขอบ card แล้วตารางกิน 100% ของ wrapper -->
          <div style="margin: 0 -1.5rem 1rem">
          <el-table
            :data="form.items"
            header-row-class-name="thead-light"
            size="small"
            style="width: 100%"
          >
            <el-table-column :label="$t('modern_trade.sale_order.product')" min-width="180">
              <template slot-scope="{ row }">
                <span v-if="row.product_code">{{ row.product_code }} - </span>{{ row.product_name }}
                <span v-if="row.wms_linked === false" class="badge badge-danger ml-1">
                  <i class="fa fa-exclamation-triangle"></i> {{ $t('modern_trade.sale_order.no_sku_badge') }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.retail_price')" min-width="100" align="right">
              <template slot-scope="{ row }">{{ money(row.retail_price) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.qty')" min-width="90" align="center">
              <template slot-scope="{ row }">
                <input v-model.number="row.qty" type="number" min="1" class="form-control form-control-sm" @input="recompute" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.unit_price')" min-width="110" align="right">
              <template slot-scope="{ row }">
                {{ money(row.unit_price) }}<br /><small class="text-muted">{{ sourceLabel(row.price_source) }}</small>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.discount')" min-width="100" align="right">
              <template slot-scope="{ row }">
                <input v-model.number="row.discount" type="number" min="0" class="form-control form-control-sm" @input="recompute" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.amount')" min-width="110" align="right">
              <template slot-scope="{ row }">{{ money(lineAmount(row)) }}</template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="70" align="center">
              <template slot-scope="{ $index }">
                <base-button size="sm" type="danger" @click.native="removeItem($index)">
                  <i class="fa fa-trash"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>
          </div>

          <!-- Totals -->
          <div class="row mt-3 justify-content-end">
            <div class="col-md-5">
              <div class="d-flex justify-content-between">
                <span class="text-muted">{{ $t("modern_trade.sale_order.sub_total") }}</span>
                <strong>{{ money(subTotal) }}</strong>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-1">
                <span class="text-muted">{{ $t("modern_trade.sale_order.bill_discount") }}</span>
                <input v-model.number="form.bill_discount" type="number" min="0" class="form-control form-control-sm text-right" style="width:120px" @input="recompute" />
              </div>
              <div class="d-flex justify-content-between mt-1">
                <span class="text-muted">{{ $t("modern_trade.sale_order.vat_amount") }} ({{ vatRate }}%)</span>
                <strong>{{ money(vatAmount) }}</strong>
              </div>
              <hr class="my-2" />
              <div class="d-flex justify-content-between">
                <span><strong>{{ $t("modern_trade.sale_order.net_amount") }}</strong></span>
                <strong class="text-primary">{{ money(netAmount) }}</strong>
              </div>
              <small class="text-muted d-block mt-1">
                {{ $t("modern_trade.sale_order.estimate_note") || "ราคาเป็นการประมาณการ ระบบจะคำนวณราคาจริงหลังบันทึก" }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer">
        <el-button @click="showDialog = false">{{ $t("close") }}</el-button>
        <el-button type="primary" :loading="saving" @click="handleCreateSubmit">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>

    <!-- Shared customer detail (#164) -->
    <customer-detail-modal
      :visible.sync="showCustomer"
      :company-id="customerId"
    ></customer-detail-modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Button, RadioGroup, Radio, Input, DatePicker } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import CustomerDetailModal from "@/components/Modern/CustomerDetailModal";
import MtDocSearchBox from "@/views/ModernTrade/MtDocSearchBox";
import mtStatusColor from "@/mixins/mtStatusColor";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

const VAT_RATE = 7;

export default {
  name: "MtSaleOrders",
  mixins: [mtStatusColor],
  components: {
    RouteBreadCrumb,
    BasePagination,
    CustomerDetailModal,
    MtDocSearchBox,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [RadioGroup.name]: RadioGroup,
    [Radio.name]: Radio,
    [Input.name]: Input,
    [DatePicker.name]: DatePicker,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    vatRate() {
      return VAT_RATE;
    },
    subTotal() {
      return this.form.items.reduce((s, r) => s + this.lineAmount(r), 0);
    },
    afterDiscount() {
      return this.subTotal - (Number(this.form.bill_discount) || 0);
    },
    vatAmount() {
      return this.round2(this.afterDiscount * (VAT_RATE / 100));
    },
    netAmount() {
      return this.afterDiscount + this.vatAmount;
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      search: "",
      filterCompany: null,
      filterSaleType: null,
      filterStatus: null,
      dateRange: null,
      companies: [],
      showDialog: false,
      poFile: null,
      productLoading: false,
      productOptions: [],
      picker: { product_id: null, qty: 1 },
      // shared customer modal
      showCustomer: false,
      customerId: null,
      form: {
        company_id: null,
        sale_type: 1,
        shipping_type: 1,
        po_number: "",
        po_date: "",
        note: "",
        bill_discount: 0,
        items: [],
      },
      companyProfile: { gp_percent: 0, vat_included: 1 },
      statusOptions: [],
    };
  },
  mounted() {
    this.buildStatusOptions();
    this.restoreFromQuery();
    this.loadCompanies();
    this.loadData();
  },
  methods: {
    // Restore filter + page from the URL query so returning from the detail
    // page (via $router.back) lands on the exact same search state (#164).
    restoreFromQuery() {
      const q = this.$route.query || {};
      if (q.search) this.search = q.search;
      if (q.company_id) this.filterCompany = Number(q.company_id) || q.company_id;
      if (q.sale_type) this.filterSaleType = Number(q.sale_type);
      if (q.status) this.filterStatus = Number(q.status);
      if (q.created_from && q.created_to) this.dateRange = [q.created_from, q.created_to];
      if (q.page) this.page = Number(q.page) || 1;
    },
    syncQuery() {
      const query = {};
      if (this.search) query.search = this.search;
      if (this.filterCompany) query.company_id = this.filterCompany;
      if (this.filterSaleType) query.sale_type = this.filterSaleType;
      if (this.filterStatus) query.status = this.filterStatus;
      if (this.dateRange && this.dateRange.length === 2) {
        query.created_from = this.dateRange[0];
        query.created_to = this.dateRange[1];
      }
      if (this.page > 1) query.page = this.page;
      this.$router.replace({ query }).catch(() => {});
    },
    // #183: open the full-screen print-set preview page instead of downloading.
    // ไอคอน PDF ตัวนี้อยู่ข้างเลขที่ใบสั่งขายในตาราง ⇒ ระบุ doc=so ให้ชัดเจน ไม่พึ่ง default
    // ของหน้า preview (ถ้าวันหลัง default เปลี่ยน ปุ่มนี้ต้องยังเปิดใบสั่งขายเหมือนเดิม)
    openPoPdf(row) {
      this.$router.push({
        name: "MtPrintPreview",
        params: { soId: String(row.id) },
        query: { so_no: row.so_no, doc: "so" },
      });
    },
    openCustomer(row) {
      this.customerId = row.company_id;
      this.showCustomer = true;
    },
    buildStatusOptions() {
      this.statusOptions = [
        { value: 1, label: this.$t("modern_trade.sale_order.status_draft") },
        { value: 2, label: this.$t("modern_trade.sale_order.status_confirmed") },
        { value: 3, label: this.$t("modern_trade.sale_order.status_in_production") },
        { value: 4, label: this.$t("modern_trade.sale_order.status_goods_received") },
        { value: 5, label: this.$t("modern_trade.sale_order.status_delivered") },
        { value: 6, label: this.$t("modern_trade.sale_order.status_closed") },
        { value: 9, label: this.$t("modern_trade.sale_order.status_cancelled") },
      ];
    },
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ page: 1, per_page: 500, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (err) {
        // Non-fatal — filter/select simply stay empty.
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.filterCompany) params.company_id = this.filterCompany;
        if (this.filterSaleType) params.sale_type = this.filterSaleType;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.search) params.search = this.search;
        if (this.dateRange && this.dateRange.length === 2) {
          params.created_from = this.dateRange[0];
          params.created_to = this.dateRange[1];
        }
        this.syncQuery();
        const res = await ModernTradeService.saleOrdersList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    onFilter() {
      this.page = 1;
      this.loadData();
    },
    goDetail(id) {
      this.$router.push({ name: "MtSaleOrderDetail", params: { id } });
    },
    openCreate() {
      this.resetForm();
      this.showDialog = true;
    },
    async onCompanyChange(id) {
      const c = this.companies.find(x => x.id === id) || {};
      this.companyProfile = {
        gp_percent: Number(c.gp_percent) || 0,
        vat_included: c.vat_included === undefined ? 1 : Number(c.vat_included),
      };
      // round-19 Dev-C: re-price every line from the server so template-bound
      // prices refresh when the company (and thus its price template) changes.
      for (const r of this.form.items) {
        await this.previewLine(r);
      }
      this.recompute();
    },
    // round-19 Dev-C: el-select remote in element-ui 2.4.11 does NOT fire
    // remote-method when the dropdown opens — preload options on open so the
    // list is populated before the user types.
    onProductDropdown(visible) {
      if (visible && !this.productOptions.length) {
        this.searchProducts("");
      }
    },
    // Fetch the server-resolved price (template-aware) for one line and stamp
    // retail_price + unit_price + source onto it. Returns the preview payload
    // (or null on failure / no-company).
    async previewLine(row) {
      if (!this.form.company_id || !row || !row.product_id) return null;
      try {
        const res = await ModernTradeService.pricePreview({
          company_id: this.form.company_id,
          product_id: row.product_id,
          qty: Number(row.qty) || 1,
          discount: Number(row.discount) || 0,
        });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        if (d.found) {
          row.retail_price = Number(d.retail_price) || 0;
          row.unit_price = Number(d.unit_price) || 0;
          row.price_source = d.source || "default";
        }
        return d;
      } catch (e) {
        return null;
      }
    },
    async searchProducts(query) {
      this.productLoading = true;
      try {
        const params = { page: 1, per_page: 50, status: 1 };
        if (query) params.search = query;
        const res = await ModernTradeService.productsList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.productOptions = d.items || [];
      } catch (err) {
        this.productOptions = [];
      } finally {
        this.productLoading = false;
      }
    },
    // A product is "linked" when the BE resolved its SKU to a WMS product.
    // Prefer the explicit wms_linked flag; fall back to wms_product_id presence.
    isWmsLinked(p) {
      if (!p) return true;
      if (p.wms_linked !== undefined && p.wms_linked !== null) return !!p.wms_linked;
      return !!(p.wms_product_id && Number(p.wms_product_id) > 0);
    },
    async addItem() {
      if (!this.picker.product_id) return;
      // round-19 Dev-C: price preview needs the company to resolve the template.
      if (!this.form.company_id) {
        swal.fire({ title: this.$t("modern_trade.sale_order.company_required"), icon: "warning" });
        return;
      }
      const p = this.productOptions.find(x => x.id === this.picker.product_id);
      if (!p) return;
      if (this.form.items.some(r => r.product_id === p.id)) {
        return;
      }
      const qty = Number(this.picker.qty) > 0 ? Number(this.picker.qty) : 1;
      // round-19 Dev-C: resolve the real (template-aware) price server-side
      // before adding. found=false → no price for this product → warn + abort.
      let preview;
      try {
        const res = await ModernTradeService.pricePreview({
          company_id: this.form.company_id,
          product_id: p.id,
          qty,
          discount: 0,
        });
        preview = (res.data && res.data.Data && res.data.Data[0]) || {};
      } catch (e) {
        swal.fire({ title: (e.response && e.response.data && e.response.data.Cause) || e.message, icon: "error" });
        return;
      }
      if (!preview.found) {
        swal.fire({ title: this.$t("modern_trade.sale_order.no_price"), icon: "warning" });
        return;
      }
      const linked = this.isWmsLinked(p);
      if (!linked) {
        // Warn but don't hard-block: the item can still be quoted; the SKU can be
        // linked later on the MT products page before goods/delivery.
        const result = await swal.fire({
          title: this.$t("modern_trade.sale_order.no_sku_confirm_title"),
          text: this.$t("modern_trade.sale_order.no_sku_confirm_text"),
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: this.$t("confirm") || "Confirm",
          cancelButtonText: this.$t("cancel") || "Cancel",
        });
        if (!result.value) return;
      }
      this.form.items.push({
        product_id: p.id,
        product_code: p.code,
        product_name: p.name,
        wms_linked: linked,
        retail_price: Number(preview.retail_price) || 0,
        qty,
        discount: 0,
        unit_price: Number(preview.unit_price) || 0,
        price_source: preview.source || "default",
      });
      this.picker = { product_id: null, qty: 1 };
      this.recompute();
    },
    removeItem(idx) {
      this.form.items.splice(idx, 1);
      this.recompute();
    },
    // round-19 Dev-C: label the per-line price source returned by price-preview.
    sourceLabel(source) {
      if (source === "template") return this.$t("modern_trade.sale_order.price_source_template");
      if (source === "default") return this.$t("modern_trade.sale_order.price_source_default");
      return this.$t("modern_trade.sale_order.estimate") || "ประมาณการ";
    },
    estimateUnit(retail) {
      const base = (Number(retail) || 0) * (1 - this.companyProfile.gp_percent / 100);
      const unit = this.companyProfile.vat_included ? base / (1 + VAT_RATE / 100) : base;
      return this.round2(unit);
    },
    lineAmount(row) {
      return (Number(row.unit_price) || 0) * (Number(row.qty) || 0) - (Number(row.discount) || 0);
    },
    recompute() {
      // computed props react automatically; force update for input events on rows.
      this.form.items = this.form.items.slice();
    },
    onPoFileChange(e) {
      this.poFile = e.target.files[0] || null;
    },
    resetForm() {
      this.poFile = null;
      this.picker = { product_id: null, qty: 1 };
      this.productOptions = [];
      this.companyProfile = { gp_percent: 0, vat_included: 1 };
      this.form = {
        company_id: null,
        sale_type: 1,
        shipping_type: 1,
        po_number: "",
        po_date: "",
        note: "",
        bill_discount: 0,
        items: [],
      };
      if (this.$refs.poFileInput) this.$refs.poFileInput.value = "";
    },
    validateForm() {
      if (!this.form.company_id) return this.$t("modern_trade.sale_order.company_required");
      if (!this.form.sale_type) return this.$t("modern_trade.sale_order.sale_type_required");
      if (!this.form.shipping_type) return this.$t("modern_trade.sale_order.shipping_type_required");
      if (!this.form.po_number) return this.$t("modern_trade.sale_order.po_number_required");
      if (!this.poFile) return this.$t("modern_trade.sale_order.po_file_required");
      if (this.poFile.size > 10 * 1024 * 1024) return this.$t("modern_trade.sale_order.po_file_too_large") || "File exceeds 10MB";
      if (!this.form.items.length) return this.$t("modern_trade.sale_order.items_required");
      return null;
    },
    async handleCreateSubmit() {
      const err = this.validateForm();
      if (err) {
        swal.fire({ title: err, icon: "warning" });
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("company_id", this.form.company_id);
        fd.append("sale_type", this.form.sale_type);
        fd.append("shipping_type", this.form.shipping_type);
        fd.append("po_number", this.form.po_number);
        if (this.form.po_date) fd.append("po_date", this.form.po_date);
        if (this.form.note) fd.append("note", this.form.note);
        fd.append("bill_discount", this.form.bill_discount || 0);
        fd.append("file", this.poFile);
        const items = this.form.items.map(r => ({
          product_id: r.product_id,
          qty: Number(r.qty) || 0,
          discount: Number(r.discount) || 0,
        }));
        fd.append("items", JSON.stringify(items));
        const res = await ModernTradeService.saleOrderCreate(fd);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        const so = d.sale_order || d.so || d;
        this.showDialog = false;
        swal.fire({ title: this.$t("modern_trade.sale_order.saved"), icon: "success", timer: 1500, showConfirmButton: false });
        if (so && so.id) {
          this.$router.push({ name: "MtSaleOrderDetail", params: { id: so.id } });
        } else {
          this.loadData();
        }
      } catch (e) {
        swal.fire({ title: (e.response && e.response.data && e.response.data.Cause) || e.message, icon: "error" });
      } finally {
        this.saving = false;
      }
    },
    saleTypeLabel(t) {
      if (t === 1) return this.$t("modern_trade.sale_order.sale_type_consignment");
      if (t === 2) return this.$t("modern_trade.sale_order.sale_type_outright");
      return "-";
    },
    shippingTypeLabel(t) {
      switch (t) {
        case 1: return this.$t("modern_trade.sale_order.shipping_type_pack");
        case 2: return this.$t("modern_trade.sale_order.shipping_type_factory");
        case 3: return this.$t("modern_trade.sale_order.shipping_type_express");
        default: return "-";
      }
    },
    statusLabel(s) {
      switch (s) {
        case 1: return this.$t("modern_trade.sale_order.status_draft");
        case 2: return this.$t("modern_trade.sale_order.status_confirmed");
        case 3: return this.$t("modern_trade.sale_order.status_in_production");
        case 4: return this.$t("modern_trade.sale_order.status_goods_received");
        case 5: return this.$t("modern_trade.sale_order.status_delivered");
        case 6: return this.$t("modern_trade.sale_order.status_closed");
        case 9: return this.$t("modern_trade.sale_order.status_cancelled");
        default: return "-";
      }
    },
    statusClass(s) {
      switch (s) {
        case 1: return "badge-secondary";
        case 2: return "badge-info";
        case 3: return "badge-warning";
        case 4: return "badge-warning";
        case 5: return "badge-primary";
        case 6: return "badge-success";
        case 9: return "badge-danger";
        default: return "badge-secondary";
      }
    },
    round2(v) {
      return Math.round((Number(v) + Number.EPSILON) * 100) / 100;
    },
    money(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
  },
};
</script>

<style scoped>
.mt-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
</style>
